import {
    dataLayerPush,
    getPostalCode,
    matchUserToLocation,
    matchUserToOverrides,
    parseAddressComponents,
    segmentTrack,
    shuffleArray,
    sortLocationsByDistance
} from "btdt/shared";
import {debug} from "btdt/config";

class LocationSearch {
    app = null
    el = null
    place = null
    autocomplete = null
    loadingEl = null
    listEl = null
    inputEl = null
    messageEl = null
    locations = []
    matchedLocations = []
    userCoords = null
    activeEl = null
    mode = null
    countryFilter = null

    constructor(el, app) {
        this.app = app
        this.el = el

        this.inputEl = this.el.querySelector('input[name="query"]')
        this.listEl = this.el.querySelector('[data-search-results]')
        this.loadingEl = this.el.querySelector('[data-search-loading]')
        this.messageEl = this.el.querySelector('[data-search-message]')

        this.mode = this.el.dataset.mode
        this.countryFilter = this.el.dataset.countryFilter

        this.app.watchState('isLoading', isLoading => {
            this.loadingEl.classList.toggle('is-loading', isLoading)
        })

        this.app.watchState('clickedMapLocation', location => {
            if (debug) console.log('search is handling clickedMapLocation change')
            this.activateLocation(location)
        })

        // creating a non reference duplicate of locations is required when more than one search component is on the same page
        this.locations = this.app.locations.map(object => ({...object}))

        if (this.countryFilter) {
            this.locations = this.locations.filter(l => l.country === this.countryFilter)
        }

        if (this.mode === 'map') {
            this.outputLocations()
        }

        this.initAutocomplete()
    }

    initAutocomplete() {
        const autocomplete = new this.app.google.maps.places.Autocomplete(this.inputEl, {
            componentRestrictions: {country: this.countryFilter ? [this.countryFilter] : ['us', 'ca']},
            fields: ["address_components", "geometry", "formatted_address"]
        });

        autocomplete.addListener("place_changed", () => {
            this.messageEl.innerHTML = ''
            const pl = autocomplete.getPlace()
            if (!pl.address_components) {
                this.messageEl.innerHTML = 'Please select an address from the dropdown'
                return
            }
            this.app.setState("userPlace", pl)
            this.handlePlaceChange(pl)
        })
    }

    handlePlaceChange(place) {
        // have to do a lot of JSON encoding/decoding to ensure coordinate types are converted to floats
        this.userPlace = JSON.parse(JSON.stringify(place))

        this.userCoords = JSON.parse(JSON.stringify({
            lat: this.userPlace?.geometry?.location?.lat,
            lng: this.userPlace?.geometry?.location?.lng
        }))
        this.updateResults().then(response => {})
    }

    async updateResults() {

        this.app.setState('isLoading', true)

        let matches = []

        if (this.userPlace) {
            // get all locations sorted by proximity
            const {lat, lng} = this.userCoords
            this.locations = sortLocationsByDistance(lat, lng, this.locations, this.app.google)

            // get best location(s) for the user
            let {
                address,
                city,
                region,
                postalCode,
                country
            } = parseAddressComponents(this.userPlace.address_components)

            if (!postalCode) {
                postalCode = await getPostalCode(lat, lng)
            }

            if (city && region && !address) {
                matches = await matchUserToOverrides(city, region, this.locations)
                if (matches.length > 0) {
                    shuffleArray(matches)
                    this.matchedLocations = matches
                }
            }

            if (!matches || matches.length === 0) {
                matches = await matchUserToLocation({city, postalCode, country, coords:{lat,lng}}, this.locations, this.app.google)

                if (this.mode === 'search') {
                    if (matches.length > 1) {
                        this.matchedLocations = matches.filter(m => parseFloat(m.distance) <= 80.5).slice(0, 2)
                    } else {
                        this.matchedLocations = matches
                    }
                } else {
                    this.matchedLocations = matches
                }
            }
        }

        this.app.setState('isLoading', false)

        if (this.mode === 'search') {
            this.outputLocations()
        } else {
            this.app.setState('clickedSearchLocationElement', this.matchedLocations[0])
        }

        await segmentTrack('Location Searched', {
            query: this.inputEl.value
        })

        await dataLayerPush('location_search', {
            'location_search_term': this.inputEl.value
        })

    }

    outputLocations() {
        this.activeEl = null
        this.listEl.innerHTML = ''

        const locations = this.mode === 'map' ? this.locations.filter(l => !l.service_area) : this.matchedLocations

        if (!locations) {
            return
        }

        locations.forEach(location => {
            const locationEl = this.buildLocationElement(location)
            location.el = locationEl
            this.listEl.appendChild(locationEl);
        })
    }

    activateLocation(location) {
        if (debug) console.log('activating location', location.location_name)

        if (this.activeEl) {
            this.activeEl.classList.remove('active')
            this.activeEl = null
        }

        const activeLocation = this.resolveLocation(location)

        if (!activeLocation?.el) {
            return
        }

        activeLocation.el.classList.add('active')

        this.activeEl = activeLocation.el
        this.listEl.scrollTop = this.activeEl.offsetTop
    }


    resolveLocation(location) {
        const target = (location.service_area && location.parent) ? location.parent : location

        let locationIndex = this.locations.findIndex(l => l.hash === target.hash)

        if (locationIndex !== -1) {
            return this.locations[locationIndex]
        }

        if (debug) console.log(`could not activate location ${location.location_name}`, location)

        return null
    }

    buildLocationElement(location) {
        const locationEl = document.createElement("section");
        const title = document.createElement("div");
        const btn = document.createElement("button");
        const phone = document.createElement("a");

        title.innerHTML = `<strong>Bin There Dump That - ${location.location_name}</strong>`
        title.classList.add('flex', 'flex-col', 'items-start')

        btn.innerHTML = 'Select'
        phone.innerHTML = location.phone
        phone.setAttribute('href', `tel:${location.phone}`)
        phone.classList.add('small-p', 'underline', 'decoration-transparent', 'hover:decoration-current')
        title.appendChild(phone)

        btn.addEventListener("click", async (e) => {
            e.stopPropagation()
            this.app.setState('clickedSearchLocationButton', location)
            this.app.setState('chosenLocation', location)

            await segmentTrack('Location Selected', {
                query: this.inputEl.value,
                affiliation: location.location_name
            })

            await dataLayerPush('location_select', {
                location: location.location_name
            })

            window.location.href = location.site
        })

        locationEl.addEventListener("click", () => {
            this.app.setState('clickedSearchLocationElement', location)
        })

        locationEl.appendChild(title)

        locationEl.appendChild(btn)

        return locationEl
    }
}

export default LocationSearch